import React, { useState, useEffect, useCallback, useContext } from "react";
import { Modal, Table, Tag } from "antd";

import AppContext from '../../../../context/Context';
import { getSignalLogs } from "../../../../api/signalApi";
import { useToast } from "components/common/custom-toast/Toast";
import { formatTimestamp } from "../utils";

const SignalLogsModal = ({ visible, onClose, signalId, signalName }) => {
	const {
		config: { isDark },
	} = useContext(AppContext);
	
	const [signalLogs, setSignalLogs] = useState([]);
	const [logsLoading, setLogsLoading] = useState(false);
	const toast = useToast();



	const fetchLogs = useCallback(async () => {
		if (!signalId || !visible) return;
		setLogsLoading(true);
		try {
			const res = await getSignalLogs(signalId);
			if (res?.code === 200 && Array.isArray(res?.data)) {
				setSignalLogs(res.data);
			} else {
				setSignalLogs([]);
				if (res?.error) {
					toast.error(res.error);
				}
			}
		} catch (error) {
			setSignalLogs([]);
			toast.error('Failed to fetch logs');
		} finally {
			setLogsLoading(false);
		}
	}, [signalId, visible, toast]);

	useEffect(() => {
		fetchLogs();
	}, [fetchLogs]);

	return (
		<Modal
			title={`Signal Logs: ${signalName || signalId}`}
			open={visible}
			onCancel={onClose}
			width={1200}
			footer={null}
			destroyOnClose
			className="signal-logs-modal"
		>
			<Table
				dataSource={signalLogs}
				loading={logsLoading}
				rowKey={(record, index) => `log-${index}`}
				size="small"
				scroll={{ x: true, y: 500 }}
				pagination={false}
				className="signal-logs-table"
				columns={[
					{
						title: 'TIMESTAMP',
						dataIndex: 'tsMicros',
						key: 'timestamp',
						align: 'left',
						sorter: (a, b) => (a.tsMicros || 0) - (b.tsMicros || 0),
						render: (tsMicros) => <span style={{ fontSize: '12px' }}>{formatTimestamp(tsMicros)}</span>,
						width: 120,
					},
					{
						title: 'LOG TYPE',
						dataIndex: 'color',
						key: 'logType',
						align: 'center',
						width: 100,
						render: (color) => {
							let tagColor = 'blue';
							let label = 'INFO';
							switch (color?.toLowerCase()) {
								case 'red':
									tagColor = 'error';
									label = 'ERROR';
									break;
								case 'yellow':
								case 'orange':
									tagColor = 'warning';
									label = 'WARNING';
									break;
								case 'green':
									tagColor = 'success';
									label = 'SUCCESS';
									break;
								default:
									tagColor = 'blue';
									label = 'INFO';
									break;
							}
							return <Tag color={tagColor}>{label}</Tag>;
						},
					},

					{
						title: 'MESSAGE',
						dataIndex: 'msg',
						key: 'message',
						sorter: (a, b) => {
							const aMsg = ((a.msg || '') + ' ' + (a.description || '')).toUpperCase();
							const bMsg = ((b.msg || '') + ' ' + (b.description || '')).toUpperCase();
							return aMsg.localeCompare(bMsg);
						},
						render: (msg, record) => {
							const description = record.description || '';
							return (
								<span style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', textAlign: 'left' }}>
									{msg || '-'}
									{description && (
										<span style={{ fontSize: '11px', color: isDark ? '#C6C6C6' : '#666', fontStyle: 'italic', textAlign: 'left' }}>
											{description}
										</span>
									)}
								</span>
							);
						},
						ellipsis: true,
					},
				]}
				locale={{ emptyText: 'No logs available' }}
			/>
		</Modal>
	);
};

export default SignalLogsModal;
